import React, { useCallback, useState } from 'react';
import { useFulfillmentContext } from '@/modules/fulfillment/context/FulfillmentContext.tsx';
import { z } from 'zod';
import { useOrganizationSuspenseQuery } from '@/clients/organization/useOrganization.ts';
import { useConstructionProjectSuspenseQuery } from '@/clients/constructionProject/useConstructionProject.ts';
import { useTranslation } from 'react-i18next';
import { useSectionsValidity } from '@/modules/fulfillment/hooks/useSectionsValidity.ts';
import { ClientInformationForm } from '@/shared/forms/ClientInformationForm/ClientInformationForm.tsx';
import { useUser } from '@/clients/users/useUsers.ts';

const ClientInformationFulfillmentSchema = z.object({
    userId: z.number(),
    manualCostCenter: z.string().nullable(),
});

type ClientInformationFulfillment = z.infer<typeof ClientInformationFulfillmentSchema>;

export const ClientInformationFormFulfillment = () => {
    const { t } = useTranslation();
    const { order, position, overrideCustomerInfo } = useFulfillmentContext();
    const { data: organization } = useOrganizationSuspenseQuery(order?.customerInfo?.organizationId);
    const { data: constructionProject } = useConstructionProjectSuspenseQuery(
        order?.customerInfo?.constructionProjectId
    );
    const { data: user } = useUser(order?.customerInfo?.userId);

    const [isFormValid, setIsFormValid] = useState(
        ClientInformationFulfillmentSchema.safeParse(order?.customerInfo).success
    );

    const handleFormChange = useCallback(
        (values: Partial<ClientInformationFulfillment>) => {
            const isValid = ClientInformationFulfillmentSchema.safeParse(values).success;

            overrideCustomerInfo(values);
            setIsFormValid(isValid);
        },
        [overrideCustomerInfo]
    );

    useSectionsValidity('client-information', isFormValid);

    if (!constructionProject || !organization) {
        return;
    }

    return (
        <ClientInformationForm
            defaultValues={{
                organizationId: order?.customerInfo?.organizationId,
                userId: order?.customerInfo?.userId,
                constructionProjectId: order?.customerInfo?.constructionProjectId,
                sourceCostCenter: position?.customerInfo.manualCostCenter ?? constructionProject?.costCenter ?? null,
                manualCostCenter: order?.customerInfo?.manualCostCenter ?? null,
            }}
            defaultValueLabels={{
                constructionProject: constructionProject?.name,
                organization: organization?.name,
                user: `${user?.firstName} ${user?.lastName}`,
                sourceCostCenter: t('sections.clientInformation.costCenter.sourceLabelOrder'),
                manualCostCenter: t('sections.clientInformation.costCenter.inputLabelOrder'),
            }}
            formRules={{
                organization: { disabled: true },
                constructionProject: { disabled: true },
                user: { disabled: true },
                costCenter: { required: organization?.costcenterMandatory },
            }}
            onChange={handleFormChange}
            showSubmitButton={false}
        />
    );
};
